import React from "react";
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpDown,
  ArrowUpFromLine,
  Banknote,
  Filter,
  LayoutGrid,
  Receipt,
  SlidersHorizontal,
  Smartphone,
  X,
} from "lucide-react";
import SidebarNavIcon from "../../app/components/ui/SidebarNavIcon";
import {
  TX_CURRENCY_FILTERS,
  TX_DESK_VIEWS,
  TX_SORT_OPTIONS,
  TX_STATUS_FILTERS,
} from "./constants";

const DESK_ICONS = {
  all: LayoutGrid,
  deposits: ArrowDownToLine,
  withdrawals: ArrowUpFromLine,
  needs_action: AlertTriangle,
  failed: Filter,
  mobile: Smartphone,
};

function FilterSection({ label, labelIcon: LabelIcon, children }) {
  return (
    <section className="am-nav-section">
      <p className="am-nav-section-label">
        {LabelIcon ? <LabelIcon size={10} strokeWidth={2.4} aria-hidden /> : null}
        {label}
      </p>
      <div className="am-nav-list">{children}</div>
    </section>
  );
}

function NavFilterItem({ active, onClick, icon, label, hint, count, countTone }) {
  return (
    <button
      type="button"
      className={`am-nav-item${active ? " am-nav-item-on" : ""}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="am-nav-item-icon">
        <SidebarNavIcon icon={icon} size={13} />
      </span>
      <span className="am-nav-item-copy">
        <strong>{label}</strong>
        {hint ? <em>{hint}</em> : null}
      </span>
      {count != null && count > 0 ? (
        <span className={`am-nav-item-count${countTone ? ` am-nav-item-count-${countTone}` : ""}`}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

export default function AdminTransactionFiltersRail({
  desk,
  status,
  currency,
  sortKey,
  summary,
  onDeskChange,
  onStatusChange,
  onCurrencyChange,
  onSortChange,
  onClear,
  activeCount,
  compact,
  onClose,
}) {
  const hasFilters = activeCount > 0;
  const statusCounts = {
    All: summary?.total ?? 0,
    Success: summary?.success ?? 0,
    Pending: summary?.pending ?? 0,
    Hold: summary?.hold ?? 0,
    Failed: summary?.failed ?? 0,
    Cancelled: summary?.cancelled ?? 0,
  };

  return (
    <aside className={`am-nav-rail${compact ? " am-nav-rail-compact" : ""}`}>
      <div className="am-nav-head">
        <div className="am-nav-title">
          <SlidersHorizontal size={14} strokeWidth={2.25} aria-hidden />
          <span>Transactions desk</span>
        </div>
        {compact && onClose ? (
          <button type="button" className="am-chrome-btn am-chrome-btn-icon" onClick={onClose} aria-label="Close filters">
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div className="am-nav-scroll">
        <FilterSection label="Desk">
          {TX_DESK_VIEWS.map((v) => (
            <NavFilterItem
              key={v.id}
              active={desk === v.id}
              onClick={() => onDeskChange(v.id)}
              icon={DESK_ICONS[v.id]}
              label={v.label}
              hint={v.hint}
              count={
                v.id === "needs_action"
                  ? summary?.needsAction
                  : v.id === "deposits"
                    ? summary?.pendingDeposits
                    : v.id === "withdrawals"
                      ? summary?.pendingWithdrawals
                      : null
              }
              countTone={
                (v.id === "needs_action" && summary?.needsAction) ||
                (v.id === "deposits" && summary?.pendingDeposits) ||
                (v.id === "withdrawals" && summary?.pendingWithdrawals)
                  ? "warn"
                  : undefined
              }
            />
          ))}
        </FilterSection>

        <FilterSection label="Sort" labelIcon={ArrowUpDown}>
          {TX_SORT_OPTIONS.map((opt) => (
            <NavFilterItem
              key={opt.value}
              active={sortKey === opt.value}
              onClick={() => onSortChange(opt.value)}
              icon={ArrowUpDown}
              label={opt.label}
            />
          ))}
        </FilterSection>

        <FilterSection label="Status" labelIcon={Filter}>
          {TX_STATUS_FILTERS.map((f) => (
            <NavFilterItem
              key={f.value}
              active={status === f.value}
              onClick={() => onStatusChange(f.value)}
              icon={Filter}
              label={f.label}
              count={statusCounts[f.value]}
            />
          ))}
        </FilterSection>

        <FilterSection label="Currency" labelIcon={Banknote}>
          {TX_CURRENCY_FILTERS.map((f) => (
            <NavFilterItem
              key={f.value}
              active={currency === f.value}
              onClick={() => onCurrencyChange(f.value)}
              icon={Receipt}
              label={f.label}
            />
          ))}
        </FilterSection>
      </div>

      {hasFilters ? (
        <button type="button" className="am-nav-clear" onClick={onClear}>
          Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
        </button>
      ) : null}
    </aside>
  );
}
